import React, { useMemo, useState } from 'react';
import { SearchIcon, FilterIcon, EyeIcon, PencilIcon, Trash2Icon, DownloadIcon, ChevronDownIcon, XIcon, MapPinIcon, CheckCircleIcon, ClockIcon, XCircleIcon, PlusIcon } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { districts, deedTypes, landTypes } from '../data/seedData';
import { RecordStatus, VerificationStatus } from '../data/types';
export function LandRecordsList() {
  const {
    state,
    dispatch,
    navigate,
    canEdit,
    canDelete,
    canCreate
  } = useApp();
  const {
    landRecords,
    filters,
    language,
    searchQuery
  } = state;
  const [showFilters, setShowFilters] = useState(false);
  const [localSearch, setLocalSearch] = useState(searchQuery || '');
  const [statusFilter, setStatusFilter] = useState<RecordStatus | ''>('');
  const [verificationFilter, setVerificationFilter] = useState<VerificationStatus | ''>('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [deedTypeFilter, setDeedTypeFilter] = useState('');
  const [landTypeFilter, setLandTypeFilter] = useState('');
  // Filter records
  const filteredRecords = useMemo(() => {
    return landRecords.filter(record => {
      // Search filter
      if (localSearch) {
        const search = localSearch.toLowerCase();
        const matchesSearch = record.landId.toLowerCase().includes(search) || record.landName.toLowerCase().includes(search) || record.deedNo.toLowerCase().includes(search) || record.mouza.toLowerCase().includes(search) || record.district.toLowerCase().includes(search) || record.upazila.toLowerCase().includes(search) || record.scheduleOfLand.cs?.khatianNo?.toLowerCase().includes(search) || record.scheduleOfLand.sa?.khatianNo?.toLowerCase().includes(search) || record.scheduleOfLand.rs?.khatianNo?.toLowerCase().includes(search) || record.scheduleOfLand.bds?.khatianNo?.toLowerCase().includes(search) || record.scheduleOfLand.cs?.dags.some(d => d.dagNo.toLowerCase().includes(search)) || record.scheduleOfLand.sa?.dags.some(d => d.dagNo.toLowerCase().includes(search)) || record.scheduleOfLand.rs?.dags.some(d => d.dagNo.toLowerCase().includes(search)) || record.litigation.some(l => l.caseNo.toLowerCase().includes(search));
        if (!matchesSearch) return false;
      }
      // Status filter
      if (statusFilter && record.status !== statusFilter) return false;
      // Verification filter
      if (verificationFilter && record.verification !== verificationFilter) return false;
      // District filter
      if (districtFilter && record.district !== districtFilter) return false;
      // Deed type filter
      if (deedTypeFilter && record.deedType !== deedTypeFilter) return false;
      // Land type filter
      if (landTypeFilter && record.landType !== landTypeFilter) return false;
      return true;
    });
  }, [landRecords, localSearch, statusFilter, verificationFilter, districtFilter, deedTypeFilter, landTypeFilter]);
  const clearFilters = () => {
    setLocalSearch('');
    setStatusFilter('');
    setVerificationFilter('');
    setDistrictFilter('');
    setDeedTypeFilter('');
    setLandTypeFilter('');
    dispatch({
      type: 'CLEAR_FILTERS'
    });
    dispatch({
      type: 'SET_SEARCH_QUERY',
      payload: ''
    });
  };
  const hasActiveFilters = localSearch || statusFilter || verificationFilter || districtFilter || deedTypeFilter || landTypeFilter;
  const getStatusBadge = (status: RecordStatus) => {
    if (status === 'Active') {
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700" data-id="element-795">
          <CheckCircleIcon className="w-3 h-3" data-id="element-796" />
          Active
        </span>;
    }
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600" data-id="element-797">
        Inactive
      </span>;
  };
  const getVerificationBadge = (verification: VerificationStatus) => {
    switch (verification) {
      case 'Verified':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700" data-id="element-798">
            <CheckCircleIcon className="w-3 h-3" data-id="element-799" />
            Verified
          </span>;
      case 'Pending':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700" data-id="element-800">
            <ClockIcon className="w-3 h-3" data-id="element-801" />
            Pending
          </span>;
      case 'Rejected':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700" data-id="element-802">
            <XCircleIcon className="w-3 h-3" data-id="element-803" />
            Rejected
          </span>;
    }
  };
  const handleDelete = (id: string, name: string) => {
    if (confirm(`Are you sure you want to delete "${name}"?`)) {
      dispatch({
        type: 'DELETE_LAND_RECORD',
        payload: id
      });
    }
  };
  return <div className="p-4 lg:p-6 space-y-4" data-id="element-804">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-805">
        <div data-id="element-806">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-807">
            {language === 'en' ? 'Land Records' : 'ভূমি রেকর্ড'}
          </h1>
          <p className="text-gray-600 mt-1" data-id="element-808">
            {filteredRecords.length}{' '}
            {language === 'en' ? 'records found' : 'রেকর্ড পাওয়া গেছে'}
          </p>
        </div>
        {canCreate() && <button onClick={() => navigate('create-record')} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-809">
            <PlusIcon className="w-5 h-5" data-id="element-810" />
            <span data-id="element-811">{language === 'en' ? 'Create New' : 'নতুন তৈরি'}</span>
          </button>}
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4" data-id="element-812">
        <div className="flex flex-col lg:flex-row gap-4" data-id="element-813">
          {/* Search */}
          <div className="flex-1 relative" data-id="element-814">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-815" />
            <input type="text" placeholder={language === 'en' ? 'Search by Land ID, Deed No, Mouza, Khatian, Dag, Case No...' : 'ভূমি আইডি, দলিল নং, মৌজা, খতিয়ান, দাগ, মামলা নং দিয়ে খুঁজুন...'} value={localSearch} onChange={e => setLocalSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-816" />
          </div>

          {/* Filter Toggle */}
          <button onClick={() => setShowFilters(!showFilters)} className={`inline-flex items-center gap-2 px-4 py-2.5 border rounded-lg text-sm font-medium transition-colors ${showFilters || hasActiveFilters ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-817">
            <FilterIcon className="w-4 h-4" data-id="element-818" />
            <span data-id="element-819">{language === 'en' ? 'Filters' : 'ফিল্টার'}</span>
            {hasActiveFilters && <span className="w-5 h-5 bg-blue-600 text-white rounded-full text-xs flex items-center justify-center" data-id="element-820">
                !
              </span>}
          </button>

          {/* Export Buttons */}
          <div className="flex gap-2" data-id="element-821">
            <button className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-822">
              <DownloadIcon className="w-4 h-4" data-id="element-823" />
              <span data-id="element-824">CSV</span>
            </button>
            <button className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-825">
              <DownloadIcon className="w-4 h-4" data-id="element-826" />
              <span data-id="element-827">PDF</span>
            </button>
          </div>
        </div>

        {/* Filter Dropdowns */}
        {showFilters && <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-828">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-id="element-829">
              <div data-id="element-830">
                <label className="block text-xs font-medium text-gray-600 mb-1" data-id="element-831">
                  Status
                </label>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as RecordStatus | '')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-832">
                  <option value="" data-id="element-833">All</option>
                  <option value="Active" data-id="element-834">Active</option>
                  <option value="Inactive" data-id="element-835">Inactive</option>
                </select>
              </div>
              <div data-id="element-836">
                <label className="block text-xs font-medium text-gray-600 mb-1" data-id="element-837">
                  Verification
                </label>
                <select value={verificationFilter} onChange={e => setVerificationFilter(e.target.value as VerificationStatus | '')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-838">
                  <option value="" data-id="element-839">All</option>
                  <option value="Verified" data-id="element-840">Verified</option>
                  <option value="Pending" data-id="element-841">Pending</option>
                  <option value="Rejected" data-id="element-842">Rejected</option>
                </select>
              </div>
              <div data-id="element-843">
                <label className="block text-xs font-medium text-gray-600 mb-1" data-id="element-844">
                  District
                </label>
                <select value={districtFilter} onChange={e => setDistrictFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-845">
                  <option value="" data-id="element-846">All</option>
                  {districts.map(d => <option key={d.id} value={d.name} data-id="element-847">
                      {d.name}
                    </option>)}
                </select>
              </div>
              <div data-id="element-848">
                <label className="block text-xs font-medium text-gray-600 mb-1" data-id="element-849">
                  Deed Type
                </label>
                <select value={deedTypeFilter} onChange={e => setDeedTypeFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-850">
                  <option value="" data-id="element-851">All</option>
                  {deedTypes.map(d => <option key={d.id} value={d.name} data-id="element-852">
                      {d.name}
                    </option>)}
                </select>
              </div>
              <div data-id="element-853">
                <label className="block text-xs font-medium text-gray-600 mb-1" data-id="element-854">
                  Land Type
                </label>
                <select value={landTypeFilter} onChange={e => setLandTypeFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-855">
                  <option value="" data-id="element-856">All</option>
                  {landTypes.map(l => <option key={l.id} value={l.name} data-id="element-857">
                      {l.name}
                    </option>)}
                </select>
              </div>
            </div>
            {hasActiveFilters && <button onClick={clearFilters} className="mt-4 inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700" data-id="element-858">
                <XIcon className="w-4 h-4" data-id="element-859" />
                Clear all filters
              </button>}
          </div>}
      </div>

      {/* Records Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-860">
        <div className="overflow-x-auto" data-id="element-861">
          <table className="w-full" data-id="element-862">
            <thead data-id="element-863">
              <tr className="bg-gray-50 border-b border-gray-200" data-id="element-864">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-865">
                  Land ID
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-866">
                  Land Name
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell" data-id="element-867">
                  District
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden lg:table-cell" data-id="element-868">
                  Mouza
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden lg:table-cell" data-id="element-869">
                  Deed No
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-870">
                  Status
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden sm:table-cell" data-id="element-871">
                  Verification
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden xl:table-cell" data-id="element-872">
                  Last Updated
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-873">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-874">
              {filteredRecords.map(record => <tr key={record.id} className="hover:bg-gray-50 transition-colors" data-id="element-875">
                  <td className="px-4 py-3" data-id="element-876">
                    <div className="flex items-center gap-2" data-id="element-877">
                      {record.location && <MapPinIcon className="w-4 h-4 text-blue-500" data-id="element-878" />}
                      <span className="font-medium text-gray-900" data-id="element-879">
                        {record.landId}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-880">
                    <span className="text-gray-900" data-id="element-881">{record.landName}</span>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell" data-id="element-882">
                    <span className="text-gray-600" data-id="element-883">{record.district}</span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell" data-id="element-884">
                    <span className="text-gray-600" data-id="element-885">{record.mouza}</span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell" data-id="element-886">
                    <span className="text-gray-600 font-mono text-sm" data-id="element-887">
                      {record.deedNo}
                    </span>
                  </td>
                  <td className="px-4 py-3" data-id="element-888">{getStatusBadge(record.status)}</td>
                  <td className="px-4 py-3 hidden sm:table-cell" data-id="element-889">
                    {getVerificationBadge(record.verification)}
                  </td>
                  <td className="px-4 py-3 hidden xl:table-cell" data-id="element-890">
                    <span className="text-gray-500 text-sm" data-id="element-891">
                      {record.lastUpdatedDate}
                    </span>
                  </td>
                  <td className="px-4 py-3" data-id="element-892">
                    <div className="flex items-center justify-end gap-1" data-id="element-893">
                      <button onClick={() => {
                    dispatch({
                      type: 'SET_SELECTED_RECORD',
                      payload: record.id
                    });
                    navigate('land-record-details', record.id);
                  }} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="View" data-id="element-894">
                        <EyeIcon className="w-4 h-4" data-id="element-895" />
                      </button>
                      {canEdit() && <button onClick={() => {
                    dispatch({
                      type: 'SET_SELECTED_RECORD',
                      payload: record.id
                    });
                    navigate('land-record-edit', record.id);
                  }} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Edit" data-id="element-896">
                          <PencilIcon className="w-4 h-4" data-id="element-897" />
                        </button>}
                      {canDelete() && <button onClick={() => handleDelete(record.id, record.landName)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete" data-id="element-898">
                          <Trash2Icon className="w-4 h-4" data-id="element-899" />
                        </button>}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {filteredRecords.length === 0 && <div className="px-4 py-12 text-center" data-id="element-900">
            <MapPinIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" data-id="element-901" />
            <p className="text-gray-500" data-id="element-902">
              {language === 'en' ? 'No records found' : 'কোনো রেকর্ড পাওয়া যায়নি'}
            </p>
            {hasActiveFilters && <button onClick={clearFilters} className="mt-2 text-sm text-blue-600 hover:text-blue-700" data-id="element-903">
                {language === 'en' ? 'Clear filters' : 'ফিল্টার মুছুন'}
              </button>}
          </div>}
      </div>
    </div>;
}